import { forwardRef, useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import { Button, Dialog } from "../../ui";
import type { ButtonProps } from "../../ui";

interface GuidedActionButtonProps extends ButtonProps {
  readonly blockedReason?: string | null;
  readonly guideItems?: readonly string[];
  readonly guideAction?: ReactNode;
}

/** Keeps a blocked business action discoverable while preventing its command from running. */
export const GuidedActionButton = forwardRef<HTMLButtonElement, GuidedActionButtonProps>(function GuidedActionButton({
  blockedReason,
  guideItems,
  guideAction,
  onClick,
  children,
  className,
  ...props
}: GuidedActionButtonProps, ref) {
  const [guideOpen, setGuideOpen] = useState(false);
  const blocked = Boolean(blockedReason);
  const label = props["aria-label"] ?? (typeof children === "string" ? children : "이 작업");

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (blocked) {
      event.preventDefault();
      setGuideOpen(true);
      return;
    }
    onClick?.(event);
  };

  return (
    <>
      <Button
        {...props}
        aria-haspopup={blocked ? "dialog" : undefined}
        aria-description={blocked ? "현재 조건에서는 실행할 수 없습니다. 누르면 이유와 다음 조치를 확인할 수 있습니다." : undefined}
        data-blocked={blocked || undefined}
        className={["admin-guided-action", className].filter(Boolean).join(" ")}
        onClick={handleClick}
        ref={ref}
      >{children}</Button>
      <Dialog
        open={guideOpen && blocked}
        title={`${label} 작업을 진행할 수 없습니다`}
        description={blockedReason ?? undefined}
        className="admin-guidance-dialog"
        onClose={() => setGuideOpen(false)}
        primaryAction={<Button size="small" onClick={() => setGuideOpen(false)}>확인</Button>}
      >{guideItems?.length || guideAction ? <>
        {guideItems?.length ? <ol className="admin-guidance-dialog__steps">{guideItems.map((item) => <li key={item}>{item}</li>)}</ol> : null}
        {guideAction}
      </> : null}</Dialog>
    </>
  );
});
